import { QueryPageWarehouse } from '@dbt-labs/integrations/productHandoffs/productHandoffURIs';

import { useDbtCloudProject } from '@/context/DbtCloudProjectProvider';
import { useDbtCloudAccount } from '@/context/DbtCloudProvider';
import { NavigationResourceType } from '@/panel/ResourcesFilter/selectedResource';
import { useIsExternal } from '../dbtStrategy/usePackage';
import { useLinkToDbtCloud } from '../links';
import {
  CanvasHandoffResourceTypes,
  HandoffDWHObjectTypes,
  isResourceSupported,
  resourceTypeToDbtObjectType,
  resourceTypeToDWHHandoffType,
  supportedDWHResourceTypes,
} from './types';

export const useCanvasDbtHandoff = ({
  resourceType,
  uniqueId,
  packageName,
  projectId,
  environmentId,
}: {
  resourceType: NavigationResourceType | 'package';
  uniqueId: string;
  packageName: string | null | undefined;
  projectId?: number;
  environmentId?: number;
}) => {
  const { projectId: inheritedProjectId, environmentId: inheritedEnvironmentId } =
    useDbtCloudProject();
  const validProjectId = projectId ?? inheritedProjectId ?? 0;
  const validEnvironmentId = environmentId ?? inheritedEnvironmentId ?? 0;
  const { accountId = 0, hasAccountFeature } = useDbtCloudAccount();
  const isEnabled = hasAccountFeature('canvas');
  const { isExternal, fetching } = useIsExternal(packageName, validProjectId);

  const getCanvasCreateLink = useLinkToDbtCloud('canvasDbtCreate');
  const getCanvasEditLink = useLinkToDbtCloud('canvasDbtEdit');

  const showCanvas = isEnabled && !isExternal;
  const showCanvasCreateLink =
    showCanvas && isResourceSupported('create', resourceType) && !!getCanvasCreateLink;

  const showCanvasEditLink =
    showCanvas && isResourceSupported('edit', resourceType) && !!getCanvasEditLink;

  const canvasCreateLink = !showCanvasCreateLink
    ? ''
    : getCanvasCreateLink({
        accountId,
        projectId: validProjectId,
        environmentId: validEnvironmentId,
        objectType:
          resourceTypeToDbtObjectType[resourceType as CanvasHandoffResourceTypes],
        objectId: uniqueId,
      });

  const canvasEditLink = !showCanvasEditLink
    ? ''
    : getCanvasEditLink({
        accountId,
        projectId: validProjectId,
        environmentId: validEnvironmentId,
        objectType:
          resourceTypeToDbtObjectType[resourceType as CanvasHandoffResourceTypes],
        objectId: uniqueId,
      });

  return {
    determiningCanvasAccess: fetching,
    showCanvasCreateLink,
    showCanvasEditLink,
    canvasCreateLink,
    canvasEditLink,
    isEnabled,
  };
};

export const useCanvasDWHHandoff = (
  resourceType: HandoffDWHObjectTypes,
  uniqueId: string,
  warehouseType: QueryPageWarehouse,
  projectId?: number,
  environmentId?: number,
) => {
  const { accountId, hasAccountFeature } = useDbtCloudAccount();
  const isEnabled = hasAccountFeature('canvas');

  const getCanvasCreateLink = useLinkToDbtCloud('canvasDWHCreate');
  const getCanvasEditLink = useLinkToDbtCloud('canvasDWHEdit');
  const isResourceTypeSupported = supportedDWHResourceTypes.includes(resourceType);

  const showCanvasCreateLink =
    isEnabled && !!getCanvasCreateLink && isResourceTypeSupported;

  const showCanvasEditLink =
    isEnabled && !!getCanvasEditLink && isResourceTypeSupported;

  const canvasCreateLink = !showCanvasCreateLink
    ? ''
    : getCanvasCreateLink({
        accountId: accountId ?? 0,
        projectId: projectId ?? 0,
        environmentId: environmentId ?? 0,
        objectType: resourceTypeToDWHHandoffType[resourceType],
        objectId: uniqueId,
        warehouseType,
      });

  const canvasEditLink = !showCanvasEditLink
    ? ''
    : getCanvasEditLink({
        accountId: accountId ?? 0,
        projectId: projectId ?? 0,
        environmentId: environmentId ?? 0,
        objectType: resourceTypeToDWHHandoffType[resourceType],
        objectId: uniqueId,
        warehouseType,
      });

  return {
    showCanvasCreateLink,
    showCanvasEditLink,
    canvasCreateLink,
    canvasEditLink,
    isEnabled,
  };
};
